import { ICellRendererParams } from '@ag-grid-community/core'
import {
  BulkGutterMark,
  BulkRowVerdict,
  bulkChangedMark,
  bulkValidityMark,
} from './bulk-transaction-grid'
import { BulkCellKind } from '../../bulk-transaction/bulk-transaction.projection'

/**
 * What a bulk cell draws at rest.
 *
 * Function renderers rather than Angular components, deliberately. A resting cell is a value and at
 * most one glyph — there is no state to hold and nothing to bind — while a component would be one
 * Angular instance per visible cell, created and destroyed on every scroll tick. The EDITOR is a
 * component (see `bulk-cell-editor.component.ts`): only one exists at a time and it carries real
 * behaviour.
 */

/** What a renderer needs from the dialog, without reaching into it. */
export interface BulkCellRendererContext {
  /** The gutter's verdict for a row: its glyph, its tone, its tooltip and its counts. */
  verdictAt(rowIndex: number): BulkRowVerdict
  /**
   * Open the value panel on an enum cell, or shut it if this cell's panel is the one already open.
   *
   * `anchor` is the cell element: the panel hangs off the cell, as the design draws it.
   */
  toggleEnum(rowIndex: number, field: string, anchor: HTMLElement): void
  /** Whether the open panel belongs to this cell — which way its chevron points. */
  enumOpen(rowIndex: number, field: string): boolean
}

/**
 * One glyph per meaning, from the prototype's `CELL_ADORN` table: lock for read-only, chevron for a
 * closed set, magnifier for a reference set, calendar for a date, spinner for a list this phase
 * cannot resolve. A number or a free-text cell carries none — there is nothing about them a glyph
 * would be telling you.
 */
const ADORNMENT: Readonly<
  Record<BulkCellKind, { icon: string; title: string } | null>
> = {
  readOnly: { icon: 'mdi-lock', title: 'Read-only' },
  // A collection's cell carries no glyph: its lines are laid out as columns from the glyph on its
  // HEADER, so a second control on every row would be a second way to do the same thing.
  collection: null,
  enum: { icon: 'mdi-chevron-down', title: 'Choose a value' },
  lookup: { icon: 'mdi-magnify', title: 'Search for a value' },
  // A date is rendered by Northfork's inline-edit component, which carries its own edit button —
  // see `inlineDateColumn`. Nothing here draws a date cell, so nothing here adorns one.
  date: null,
  number: null,
  text: null,
}

/** Row index out of the stub ag-grid holds, or null when there is no row behind the cell. */
const indexOf = (params: ICellRendererParams): number | null => {
  const index = (params.data as { index?: number } | undefined)?.index
  return typeof index === 'number' ? index : null
}

/**
 * Give a glyph the whole press, not half of it.
 *
 * A press is two events and the grid reads them in two places: it moves the range on `mousedown`,
 * and `singleClickEdit` starts the editor from `onCellClicked` — on `click`. Both arrive through one
 * delegated listener on the row container, so stopping the event at the glyph is what keeps the grid
 * out of a press the glyph has already answered.
 *
 * Stopping only the `mousedown` left the cell editing underneath the panel that had just opened over
 * it, and an editing cell is one `CellCtrl.refreshCell` returns early from — so a day picked in the
 * calendar was written to the model, never repainted, and then overwritten by the editor's own stale
 * value when it closed.
 */
function claimPress(glyph: HTMLElement, act: () => void): void {
  glyph.addEventListener('mousedown', event => {
    event.preventDefault()
    event.stopPropagation()
    act()
  })
  glyph.addEventListener('click', event => event.stopPropagation())
}

/**
 * The resting cell: its value, and the glyph that says what kind of cell it is.
 *
 * A collection's summary has no glyph of its own — see `ADORNMENT`. Its value is inked in the
 * stylesheet, which is where the design's blue lives.
 */
export function bulkCellRenderer(
  kind: BulkCellKind,
  context: BulkCellRendererContext,
): (params: ICellRendererParams) => HTMLElement {
  const adornment = ADORNMENT[kind]

  return (params: ICellRendererParams) => {
    const cell = document.createElement('span')
    cell.className = 'bt-ag-cell'

    const value = document.createElement('span')
    value.className = 'bt-ag-value'
    value.textContent = params.value == null ? '' : String(params.value)
    cell.appendChild(value)

    if (!adornment) return cell

    const glyph = document.createElement('i')
    glyph.title = adornment.title
    const field = params.colDef?.field

    if (kind === 'enum' && field) {
      const index = indexOf(params)
      const open = index !== null && context.enumOpen(index, field)
      // The chevron reports the panel's state, so it points at where the list currently is.
      glyph.className = `mdi ${
        open ? 'mdi-chevron-up' : adornment.icon
      } bt-ag-adorn bt-ag-pick vp-trigger`
      glyph.setAttribute('role', 'button')
      claimPress(glyph, () => {
        if (index !== null) context.toggleEnum(index, field, params.eGridCell)
      })
    } else {
      glyph.className = `mdi ${adornment.icon} bt-ag-adorn`
    }

    cell.appendChild(glyph)
    return cell
  }
}

/**
 * The status gutter: how the row stands, as one glyph.
 *
 * Reads the verdict live rather than through a value getter, because it is a fact about the row's
 * edits rather than about any column — ag-grid refreshes it when the cell is told to, which the
 * dialog does after a write.
 */
/** One gutter mark, drawn the same way whichever column it belongs to. */
const gutterMark = (mark: BulkGutterMark): HTMLElement => {
  const cell = document.createElement('span')
  cell.className = `bt-ag-verdict ${mark.tone}`
  cell.title = mark.label

  const glyph = document.createElement('i')
  glyph.className = `mdi ${mark.icon}`
  cell.appendChild(glyph)

  if (mark.count !== null) {
    const count = document.createElement('span')
    count.className = 'bt-ag-verdict-count'
    count.textContent = String(mark.count)
    cell.appendChild(count)
  }
  return cell
}

/**
 * The validity gutter: whether the row can be posted, and how many values it is short of.
 *
 * How many, beside which — the column answers "is this row short?" and "by how much?" in one
 * reading, so a row needing eight values is told apart from one needing one without hovering.
 */
export function bulkStatusRenderer(
  context: BulkCellRendererContext,
): (params: ICellRendererParams) => HTMLElement {
  return (params: ICellRendererParams) => {
    const index = indexOf(params)
    if (index === null) return document.createElement('span')
    return gutterMark(bulkValidityMark(context.verdictAt(index).missing))
  }
}

/**
 * The changed gutter, beside the validity one rather than instead of it.
 *
 * A row can be both incomplete and edited, and both are worth saying: folding them into one column
 * meant the edit count disappeared the moment the row was also missing something.
 */
export function bulkChangedRenderer(
  context: BulkCellRendererContext,
): (params: ICellRendererParams) => HTMLElement {
  return (params: ICellRendererParams) => {
    const index = indexOf(params)
    const mark =
      index === null ? null : bulkChangedMark(context.verdictAt(index).changed)
    return mark ? gutterMark(mark) : document.createElement('span')
  }
}
